/**
 * features/attendance/PerClassOverview.tsx
 * ------------------------------------------------------------------
 * Round 13 — Per-class attendance summary.
 *
 * Visible to every staff role except parent (decision Q1=option 3).
 *
 * Top: table of classes with this-term attendance % per class.
 * Click a class to drill down: list students sorted by absent days
 * descending (worst first) with per-student totals.
 *
 * All client-side aggregation from reportDailyAttendance + computeClassAttendance.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { ChevronRight, ChevronLeft, Users } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { Spinner, Card, Button } from '@/components/ui/primitives';
import { GradientHero } from '@/components/ui/glass';
import { computeClassAttendance } from '@/lib/attendanceCalc';
import type { DailyAttendanceDoc } from '@/types';

export default function PerClassOverview() {
  const { classes, activeStudents: students } = useData();
  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState<DailyAttendanceDoc[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const snap = await getDocs(collection(db, COLLECTIONS.dailyAttendance));
        if (cancelled) return;
        const all: DailyAttendanceDoc[] = snap.docs.map(
          (d) => ({ id: d.id, ...d.data() }) as DailyAttendanceDoc,
        );
        setDocs(all);
        setError(null);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ---- per-class summary ---- */
  const perClass = useMemo(() => {
    return classes.map((cls) => {
      const classDocs = docs.filter((d) => d.classId === cls.id);
      const classStudents = students.filter((s) => s.currentClass === cls.id);
      const summary = computeClassAttendance(
        cls.id,
        classDocs,
        classStudents.map((s) => s.id),
      );
      const worst = summary.students
        .filter((s) => s.attendancePercent !== null)
        .sort((a, b) => (a.attendancePercent ?? 100) - (b.attendancePercent ?? 100))[0];
      return {
        classId: cls.id,
        className: cls.name,
        enrolled: classStudents.length,
        daysRecorded: classDocs.length,
        averagePct: summary.averageAttendancePercent,
        worstAttendance: worst,
        summary,
      };
    });
  }, [classes, students, docs]);

  /* ---- drill-down view ---- */
  const drill = useMemo(() => {
    if (!selectedClassId) return null;
    const row = perClass.find((r) => r.classId === selectedClassId);
    if (!row) return null;
    // Sort students by absent days desc (worst first), then by attendance % asc
    const ranked = [...row.summary.students]
      .map((s) => {
        const studentRec = students.find((st) => st.id === s.studentId);
        return {
          ...s,
          fullName: studentRec?.fullName ?? '(unknown)',
        };
      })
      .sort((a, b) => {
        if (b.absent !== a.absent) return b.absent - a.absent;
        return (a.attendancePercent ?? 100) - (b.attendancePercent ?? 100);
      });
    return { row, ranked };
  }, [selectedClassId, perClass, students]);

  if (loading) {
    return (
      <div className="p-8">
        <Spinner label="Loading attendance data…" />
      </div>
    );
  }
  if (error) {
    return (
      <Card className="p-4 m-4 text-red-700 bg-red-50 border-red-200">
        <p className="font-medium">Failed to load attendance.</p>
        <p className="text-sm mt-1">{error}</p>
      </Card>
    );
  }

  /* ---- DRILL-DOWN MODE ---- */
  if (drill) {
    return (
      <div className="space-y-4 px-1">
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setSelectedClassId(null)}>
            <ChevronLeft size={16} className="mr-1" />
            Back to classes
          </Button>
        </div>
        <GradientHero
          title={`${drill.row.className} — student attendance`}
          subtitle={`${drill.row.enrolled} students · ${drill.row.daysRecorded} days recorded · Avg ${drill.row.averagePct ?? '—'}%`}
          icon={Users}
        />
        {drill.ranked.length === 0 ? (
          <Card className="p-6 text-center text-gray-500">
            No students in this class.
          </Card>
        ) : (
          <Card className="p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-left">
                <tr>
                  <th className="px-4 py-2 font-semibold text-gray-700 dark:text-gray-200">Student</th>
                  <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-200 text-right">Attendance</th>
                  <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-200 text-right">Days</th>
                  <th className="px-3 py-2 font-semibold text-red-700 dark:text-red-300 text-right">Absent</th>
                  <th className="px-3 py-2 font-semibold text-amber-700 dark:text-amber-300 text-right">Late</th>
                  <th className="px-3 py-2 font-semibold text-blue-700 dark:text-blue-300 text-right">Excused</th>
                  <th className="px-3 py-2 font-semibold text-purple-700 dark:text-purple-300 text-right">Sick</th>
                </tr>
              </thead>
              <tbody>
                {drill.ranked.map((s, idx) => {
                  const pctClass =
                    s.attendancePercent === null
                      ? 'text-gray-500'
                      : s.attendancePercent >= 95
                        ? 'text-emerald-700 dark:text-emerald-300'
                        : s.attendancePercent >= 85
                          ? 'text-blue-700 dark:text-blue-300'
                          : 'text-red-700 dark:text-red-300';
                  return (
                    <tr
                      key={s.studentId}
                      className={
                        idx % 2 === 0
                          ? 'bg-white dark:bg-gray-900'
                          : 'bg-gray-50/60 dark:bg-gray-800/30'
                      }
                    >
                      <td className="px-4 py-2">{s.fullName}</td>
                      <td className={`px-3 py-2 text-right font-semibold ${pctClass}`}>
                        {s.attendancePercent === null ? '—' : `${s.attendancePercent}%`}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400">{s.totalDays}</td>
                      <td className="px-3 py-2 text-right">{s.absent || ''}</td>
                      <td className="px-3 py-2 text-right">{s.late || ''}</td>
                      <td className="px-3 py-2 text-right">{s.excused || ''}</td>
                      <td className="px-3 py-2 text-right">{s.sick || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    );
  }

  /* ---- TABLE MODE ---- */
  return (
    <div className="space-y-4 px-1">
      <GradientHero
        title="Per-class attendance"
        subtitle={`${classes.length} classes · ${docs.length} attendance days logged`}
        icon={Users}
      />
      {perClass.length === 0 ? (
        <Card className="p-6 text-center text-gray-500">No classes set up yet.</Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left">
              <tr>
                <th className="px-4 py-2 font-semibold text-gray-700 dark:text-gray-200">Class</th>
                <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-200 text-right">Enrolled</th>
                <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-200 text-right">Days logged</th>
                <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-200 text-right">Avg attendance</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {perClass.map((row, idx) => {
                const pctClass =
                  row.averagePct === null
                    ? 'text-gray-500'
                    : row.averagePct >= 95
                      ? 'text-emerald-700 dark:text-emerald-300'
                      : row.averagePct >= 85
                        ? 'text-blue-700 dark:text-blue-300'
                        : 'text-red-700 dark:text-red-300';
                return (
                  <tr
                    key={row.classId}
                    className={
                      idx % 2 === 0
                        ? 'bg-white dark:bg-gray-900'
                        : 'bg-gray-50/60 dark:bg-gray-800/30'
                    }
                  >
                    <td className="px-4 py-2 font-medium">{row.className}</td>
                    <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400">{row.enrolled}</td>
                    <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400">{row.daysRecorded}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${pctClass}`}>
                      {row.averagePct === null ? '—' : `${row.averagePct}%`}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        variant="secondary"
                        onClick={() => setSelectedClassId(row.classId)}
                        disabled={row.enrolled === 0}
                      >
                        View students
                        <ChevronRight size={14} className="ml-1" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
